// Lista de registros ya diligenciados de una inspección, con % de
// cumplimiento, nivel de criticidad, ubicación y detalle pregunta/respuesta.
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader, BarChart2, ChevronDown, MapPin } from "lucide-react";
import { api } from "../api";
import { formatDate } from "../utils/dates";
import { calculatedColumnLabel, calculateDateValue } from "../utils/dateCalculations";

const CRITICALITY_STYLE = {
  Critico: { bg: "#FEE2E2", color: "#C53030", border: "#FCA5A5" },
  Medio:   { bg: "#FEF3C7", color: "#92400E", border: "#FDE68A" },
  Bueno:   { bg: "#F0FDFA", color: "#00A896", border: "#99E8DE" },
};

const COMPLIANCE_LABELS = {
  cumple: "Cumple",
  no_cumple: "No cumple",
  cumple_parcial: "Cumple parcialmente",
  no_aplica: "No aplica",
};

function answerText(question, value) {
  if (value === undefined || value === null || value === "") return "—";
  if (question.type === "compliance") return COMPLIANCE_LABELS[value] || String(value);
  if (typeof value === "object" && "originalname" in value) return value.originalname;
  if (question.type === "boolean") return value ? "Si" : "No";
  return String(value);
}

function RecordCard({ record, fields }) {
  const [open, setOpen] = useState(false);
  const style = CRITICALITY_STYLE[record.criticality] || CRITICALITY_STYLE.Medio;

  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 14, background: "rgba(255,255,255,0.7)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, cursor: "pointer", flexWrap: "wrap" }} onClick={() => setOpen(v => !v)}>
        <div style={{ minWidth: 0 }}>
          <p style={{ fontWeight: 600, fontSize: "0.86rem" }}>{formatDate(record.submitted_at)}</p>
          <p style={{ fontSize: "0.74rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 4, marginTop: 2, flexWrap: "wrap" }}>
            <MapPin size={12} /> {Number(record.latitude).toFixed(5)}, {Number(record.longitude).toFixed(5)}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>{record.score_percent}%</span>
          <span style={{ fontSize: "0.68rem", fontWeight: 600, padding: "3px 9px", borderRadius: 20, background: style.bg, color: style.color, border: `1px solid ${style.border}` }}>
            {record.criticality}
          </span>
          <ChevronDown size={16} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s", color: "var(--text-muted)" }} />
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            style={{ overflow: "hidden" }}
          >
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {fields.map((field) => {
                const answer = record.answers[field.id] || {};
                return (
                  <div key={field.id} style={{ fontSize: "0.8rem" }}>
                    <span style={{ color: "var(--text-muted)" }}>{field.label}: </span>
                    <span style={{ fontWeight: 500 }}>{answerText(field, answer.value)}</span>
                    {calculatedColumnLabel(field) && answer.value && (
                      <div style={{ marginTop: 2 }}>
                        <span style={{ color: "var(--text-muted)" }}>{calculatedColumnLabel(field)}: </span>
                        <span style={{ fontWeight: 600, color: "var(--primary)" }}>
                          {calculateDateValue(answer.value, field.dateCalculation, record.submitted_at)}
                        </span>
                      </div>
                    )}
                    {field.type === "compliance" && field.followUps?.map((f) => {
                      const followValue = answer.followUps ? answer.followUps[f.id] : undefined;
                      if (followValue === undefined || followValue === null || followValue === "") return null;
                      return (
                        <div key={f.id} style={{ marginLeft: 14, marginTop: 2, color: "var(--text-muted)" }}>
                          ↳ {f.label}: {answerText(f, followValue)}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function InspectionResultsModal({ inspection, companyId, onClose }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getInspectionRecords(inspection.id, companyId).then((data) => {
      setRecords(Array.isArray(data) ? data : []);
      setLoading(false);
    });
  }, [inspection.id, companyId]);

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <motion.div
        className="modal modal-large"
        style={{ maxWidth: 720 }}
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 12 }}
        transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.7 }}
      >
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "#FEF3C7", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <BarChart2 size={18} style={{ color: "#B45309" }} />
            </div>
            <div>
              <h2>Resultados{inspection.name ? ` - ${inspection.name}` : ""}</h2>
              <p style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                {records.length} registro{records.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        <div style={{ padding: "8px 24px 24px", maxHeight: "65vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
              <Loader size={28} className="spin" opacity={0.4} />
            </div>
          ) : records.length === 0 ? (
            <div className="empty-state">
              <BarChart2 size={40} opacity={0.3} />
              <p>Todavia no hay registros para esta inspeccion.</p>
            </div>
          ) : (
            records.map((record) => <RecordCard key={record.id} record={record} fields={inspection.fields || []} />)
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
